const PRIORITY_ORDER = Object.freeze({ info: 0, medium: 1, high: 2, critical: 3 });

const CATEGORY_LABELS = Object.freeze({
  agent: "Agenten",
  pr: "Pull Requests",
  approval: "Freigaben",
  pipeline: "Pipeline",
  security: "Sicherheit",
  portfolio: "Portfolio",
  market: "Markt",
  learning: "Lernen",
  system: "System",
});

const VIEW_TARGETS = new Set([
  "pulse",
  "intelligence",
  "capital",
  "portfolio",
  "agents",
  "operations",
  "learning",
  "proof",
  "expert",
]);

function firstText(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function safeTimestamp(value) {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

export function normalizeNotificationPriority(value) {
  const priority = String(value || "info").trim().toLowerCase();
  if (["critical", "urgent", "emergency", "fatal"].includes(priority)) return "critical";
  if (["high", "error", "danger", "severe"].includes(priority)) return "high";
  if (["medium", "normal", "warning", "warn", "caution"].includes(priority)) return "medium";
  return "info";
}

export function inferNotificationCategory(input = {}) {
  const explicit = String(input.category || "").trim().toLowerCase();
  if (CATEGORY_LABELS[explicit]) return explicit;

  const haystack = [
    input.kind,
    input.type,
    input.actionType,
    input.title,
    input.reason,
  ].filter(Boolean).join(" ").toLowerCase();

  if (/pull.?request|\bpr\b|github/.test(haystack)) return "pr";
  if (/approval|freigabe|manual.?review|waiting/.test(haystack)) return "approval";
  if (/agent|conference|deepseek|openai|claude|gemini|qwen|\bv0\b/.test(haystack)) return "agent";
  if (/portfolio|position|capital|rebalanc|risk/.test(haystack)) return "portfolio";
  if (/discovery|market|signal|price|forecast/.test(haystack)) return "market";
  if (/learn|weight|shadow|outcome/.test(haystack)) return "learning";
  if (/pipeline|ingestion|job|data.?repair|runtime|sentinel/.test(haystack)) return "pipeline";
  if (/security|guardian|auth|attack|breach/.test(haystack)) return "security";
  return "system";
}

export function inferNotificationTarget(category, input = {}) {
  const explicit = firstText(input.targetView, input.action?.targetView);
  if (explicit && VIEW_TARGETS.has(explicit)) return explicit;
  if (category === "agent" || category === "pr" || category === "approval") return "agents";
  if (category === "portfolio") return "portfolio";
  if (category === "market") return "intelligence";
  if (category === "learning") return "learning";
  if (category === "pipeline" || category === "security") return "operations";
  return "pulse";
}

export function isSafeNotificationUrl(value, origin = null) {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const base = origin || (typeof window !== "undefined" ? window.location.origin : "https://hqs.invalid");
    const parsed = new URL(value, base);
    return parsed.protocol === "https:" || (parsed.protocol === "http:" && parsed.hostname === "localhost");
  } catch {
    return false;
  }
}

function normalizeAction(input, category) {
  const url = firstText(
    input.action?.url,
    input.url,
    input.pullRequestUrl,
    input.metadata?.pullRequestUrl,
  );
  const targetView = inferNotificationTarget(category, input);
  const label = firstText(input.action?.label, input.actionLabel)
    || (url ? "Öffnen" : "Im Dashboard prüfen");

  return {
    type: url && isSafeNotificationUrl(url) ? "external" : "navigate",
    label,
    targetView,
    url: url && isSafeNotificationUrl(url) ? url : null,
    agentId: firstText(input.action?.agentId, input.agentId, input.metadata?.agentId),
  };
}

export function notificationFingerprint(input = {}) {
  const source = firstText(input.source, "local") || "local";
  const sourceId = input.sourceId ?? input.backendId ?? input.id;
  if (sourceId !== undefined && sourceId !== null && String(sourceId).trim()) {
    return `${source}:${String(sourceId).trim()}`;
  }
  const title = firstText(input.title, input.symbol, "HQS") || "HQS";
  const message = firstText(input.message, input.body, "") || "";
  const minute = safeTimestamp(input.createdAt || input.timestamp).slice(0, 16);
  return `${source}:${title}:${message}:${minute}`.toLowerCase();
}

export function normalizeNotification(input = {}, options = {}) {
  const source = firstText(input.source, options.source, input.backendId != null ? "backend" : "local") || "local";
  const backendIdValue = input.backendId ?? (source === "backend" ? input.id : null);
  const backendId = Number.isFinite(Number(backendIdValue)) && Number(backendIdValue) > 0
    ? Number(backendIdValue)
    : null;
  const createdAt = safeTimestamp(input.createdAt || input.timestamp || input.deliveredAt);
  const category = inferNotificationCategory(input);
  const priority = normalizeNotificationPriority(input.priority || input.severity || input.level);
  const title = firstText(input.title, input.symbol, CATEGORY_LABELS[category], "HQS Meldung") || "HQS Meldung";
  const message = firstText(input.message, input.body, input.description, input.reason, "Keine weiteren Details.") || "Keine weiteren Details.";
  const sourceId = input.sourceId ?? backendId ?? input.id ?? null;
  const fingerprint = firstText(input.fingerprint) || notificationFingerprint({ ...input, source, sourceId, title, message, createdAt });

  return {
    id: firstText(input.clientId)
      || (backendId ? `backend:${backendId}` : `local:${fingerprint}`),
    backendId,
    sourceId,
    fingerprint,
    source,
    sourceLabel: firstText(input.sourceLabel)
      || (source === "backend" ? "Railway Notification Store" : source === "backend_sentinel" ? "Railway Sentinel" : source === "agent_task" ? "Agent Task Runtime" : "Lokaler HQS Status"),
    title,
    message,
    symbol: firstText(input.symbol, input.metadata?.symbol),
    type: firstText(input.type, input.kind, category) || category,
    category,
    categoryLabel: CATEGORY_LABELS[category],
    priority,
    createdAt,
    timestamp: createdAt,
    deliveredAt: input.deliveredAt ? safeTimestamp(input.deliveredAt) : null,
    seenAt: input.seenAt ? safeTimestamp(input.seenAt) : null,
    actedAt: input.actedAt ? safeTimestamp(input.actedAt) : null,
    dismissedAt: input.dismissedAt ? safeTimestamp(input.dismissedAt) : null,
    read: Boolean(input.read ?? input.isRead ?? input.seenAt),
    reason: firstText(input.reason),
    kind: firstText(input.kind, input.type, category) || category,
    actionType: firstText(input.actionType),
    followUpOutcome: firstText(input.followUpOutcome),
    requiresAction: Boolean(
      input.requiresAction
      || input.requiresUserApproval
      || input.followUpOutcome
      || ["high", "critical"].includes(priority)
      || category === "approval"
    ),
    action: normalizeAction(input, category),
    metadata: input.metadata && typeof input.metadata === "object" ? { ...input.metadata } : {},
  };
}

export function mergeNotifications(current = [], incoming = [], maxItems = 200) {
  const byFingerprint = new Map();
  for (const item of Array.isArray(current) ? current : []) {
    const normalized = normalizeNotification(item);
    byFingerprint.set(normalized.fingerprint, normalized);
  }

  for (const item of Array.isArray(incoming) ? incoming : []) {
    const normalized = normalizeNotification(item);
    const existing = byFingerprint.get(normalized.fingerprint);
    byFingerprint.set(normalized.fingerprint, existing ? {
      ...existing,
      ...normalized,
      read: Boolean(existing.read || normalized.read),
      seenAt: normalized.seenAt || existing.seenAt,
      actedAt: normalized.actedAt || existing.actedAt,
      dismissedAt: normalized.dismissedAt || existing.dismissedAt,
      metadata: { ...existing.metadata, ...normalized.metadata },
    } : normalized);
  }

  return [...byFingerprint.values()]
    .sort((a, b) => {
      const priorityDiff = PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority];
      if (priorityDiff !== 0 && Math.abs(Date.parse(b.createdAt) - Date.parse(a.createdAt)) < 60_000) return priorityDiff;
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    })
    .slice(0, Math.max(1, maxItems));
}

export function selectNewNotifications(current = [], incoming = []) {
  const known = new Set((Array.isArray(current) ? current : []).map((item) => normalizeNotification(item).fingerprint));
  return (Array.isArray(incoming) ? incoming : [])
    .map((item) => normalizeNotification(item))
    .filter((item) => !known.has(item.fingerprint));
}

export function shouldAnnounceNotification(notification, preferences = {}) {
  const item = normalizeNotification(notification);
  if (preferences.inAppAlerts === false || item.dismissedAt) return false;
  const threshold = normalizeNotificationPriority(preferences.minimumPriority || "info");
  if (PRIORITY_ORDER[item.priority] < PRIORITY_ORDER[threshold]) return false;
  if (item.category === "market" && preferences.signalAlerts === false) return false;
  if (item.category === "portfolio" && preferences.priceAlerts === false) return false;
  if (["agent", "pr", "approval"].includes(item.category) && preferences.agentAlerts === false) return false;
  if (["pipeline", "system", "security"].includes(item.category) && preferences.systemAlerts === false) return false;
  return true;
}

export function summarizeNotifications(alerts = []) {
  const active = (Array.isArray(alerts) ? alerts : []).filter((item) => !item.dismissedAt);
  return active.reduce((summary, raw) => {
    const item = normalizeNotification(raw);
    summary.total += 1;
    if (!item.read) summary.unread += 1;
    if (!item.read && item.priority === "critical") summary.critical += 1;
    if (!item.read && item.requiresAction) summary.actionRequired += 1;
    return summary;
  }, { total: 0, unread: 0, critical: 0, actionRequired: 0 });
}

export function notificationPriorityRank(value) {
  return PRIORITY_ORDER[normalizeNotificationPriority(value)];
}

export { CATEGORY_LABELS };
